import { Box, Typography, colors } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowRightAltSharp } from "@mui/icons-material";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { formatMoney } from "../../utils/helper";
import CardBase from "../global/CardBase";

const BalanceCard = () => {
  const user = useCurrentUser();
  const navigate = useNavigate();

  return (
    <CardBase
      title="Balances"
      useGlassCard={true}
      nested={false}
      gradientColor={colors.green[100]}
      actions={
        <Box
          component="button"
          onClick={() => navigate("/finances")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            color: colors.green[600],
            fontSize: { xs: "0.7rem", sm: "0.875rem" },
            fontWeight: 500,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              color: colors.green[700],
              gap: 1,
              "& .arrow-icon": {
                transform: "translateX(4px)",
              },
            },
          }}
        >
          <Typography
            component="span"
            sx={{
              color: "inherit",
              fontSize: "inherit",
              fontWeight: "inherit",
            }}
          >
            More detail
          </Typography>
          <ArrowRightAltSharp
            className="arrow-icon"
            sx={{
              fontSize: { xs: "0.85rem", sm: "1rem" },
              transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
        </Box>
      }
      sx={{
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        maxWidth: { xs: "80%", sm: "none", md: "120%", lg: "none" },
        width: { xs: "100%", sm: "100%", md: "120%", lg: "100%" },
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 12px 40px rgba(0, 0, 0, 0.5)"
              : "0 12px 40px rgba(0, 0, 0, 0.15)",
        },
        "& .MuiCardContent-root": {
          p: { xs: 1.5, sm: 2, md: 2.5 },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "flex-start", sm: "space-between" },
          minHeight: { xs: "70px", sm: "120px" },
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 0.5, sm: 1.5 }, zIndex: 1, flex: 1 }}>
          <Typography
            variant="h3"
            component="div"
            sx={{
              fontWeight: 800,
              color: colors.green[700],
              fontSize: { xs: "1.2rem", sm: "2rem", md: "2.5rem" },
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            ${formatMoney(user?.company?.balance_decrypt || 0)}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.65rem", sm: 14 }, fontWeight: 500 }}
          >
            Available Balance
          </Typography>
        </Box>
        <Box
          sx={{
            position: "absolute",
            right: { xs: -30, sm: -20, md: 10, lg: -10 },
            top: "50%",
            transform: "translateY(-50%)",
            opacity: { xs: 0.15, sm: 0.2 },
            zIndex: 0,
            width: { xs: "100px", sm: "140px", md: "280px", lg: "220px" },
            height: { xs: "100px", sm: "140px", md: "280px", lg: "220px" },
            display: { xs: "none", sm: "none", md: "none", lg: "block" },
            animation: "walletFloat 3s ease-in-out infinite, walletFadeIn 1s ease-in",
            "@keyframes walletFloat": {
              "0%, 100%": {
                transform: "translateY(-50%) translateY(0px)",
              },
              "50%": {
                transform: "translateY(-50%) translateY(-8px)",
              },
            },
            "@keyframes walletFadeIn": {
              "0%": {
                opacity: 0,
                transform: "translateY(-50%) scale(0.9)",
              },
              "100%": {
                opacity: 0.2,
                transform: "translateY(-50%) scale(1)",
              },
            },
            "& svg": {
              width: "100%",
              height: "100%",
            },
          }}
        >
          <svg
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="walletGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#10B981" stopOpacity="0.25">
                  <animate
                    attributeName="stop-opacity"
                    values="0.25;0.35;0.25"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="50%" stopColor="#34D399" stopOpacity="0.3">
                  <animate
                    attributeName="stop-opacity"
                    values="0.3;0.4;0.3"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="100%" stopColor="#6EE7B7" stopOpacity="0.25">
                  <animate
                    attributeName="stop-opacity"
                    values="0.25;0.35;0.25"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </stop>
              </linearGradient>
              <linearGradient id="walletBorder" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#10B981" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#059669" stopOpacity="0.5" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur">
                  <animate
                    attributeName="stdDeviation"
                    values="4;6;4"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </feGaussianBlur>
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <rect
              x="50"
              y="70"
              width="100"
              height="60"
              rx="8"
              fill="url(#walletGradient)"
              stroke="url(#walletBorder)"
              strokeWidth="1.5"
              filter="url(#glow)"
            >
              <animateTransform
                attributeName="transform"
                type="scale"
                values="1;1.02;1"
                dur="2s"
                repeatCount="indefinite"
              />
            </rect>

            <line
              x1="50"
              y1="100"
              x2="150"
              y2="100"
              stroke="#10B981"
              strokeWidth="1.5"
              opacity="0.4"
            />

            <text
              x="100"
              y="115"
              fontSize="42"
              fill="#059669"
              textAnchor="middle"
              fontWeight="800"
              opacity="0.6"
              fontFamily="Arial, sans-serif"
            >
              <animate
                attributeName="opacity"
                values="0.6;0.8;0.6"
                dur="1.5s"
                repeatCount="indefinite"
              />
              $
            </text>
          </svg>
        </Box>
      </Box>
    </CardBase>
  );
};

export default BalanceCard;

